import { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Platform, Modal, TouchableWithoutFeedback } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Picker } from '@react-native-picker/picker';
import {
  Users, Phone, Calendar, ChevronDown, ChevronUp, X, Search, Filter, ClipboardList,
} from '../../lib/icons';
import { createTestUser, supabase } from '../../lib/supabase';
import ServiceRequestsTab from './ServiceRequestsTab';
import VisitsTab from './VisitsTab';
import PipelineView from './PipelineView';
import MyDayTab from './MyDayTab';
import ServiceProvidersTab from './ServiceProvidersTab';
import MarketResearchQuotes from './MarketResearchQuotes';
import PropertyImport from './PropertyImport';
import TeamTab from './TeamTab';
import ReportsTab from './ReportsTab';
import ReportsPerformance from './ReportsPerformance';
import ReportsStandards from './ReportsStandards';
import ConversationsTab from './ConversationsTab';
import ReferralsTab from './ReferralsTab';
import WalkthroughAvailabilityTab from './WalkthroughAvailabilityTab';
import HomesTab from './HomesTab';
import ExpensesTab from './ExpensesTab';
import AnnouncementsTab from './AnnouncementsTab';
import TasksTab from './TasksTab';
import ConfigurationTab from './ConfigurationTab';
import AgentsTab from './AgentsTab';
import ApiKeysTab from './ApiKeysTab';
import { Profile } from './types';
import { shadows } from '../../lib/styles';
import AIAdminAssistant from '../../components/AIAdminAssistant';
import AdminLayout, {
  ADMIN_TAB_OPTIONS,
  REPORTS_VIEW_OPTIONS,
  type AdminTabValue,
  type ReportsViewValue,
} from './AdminLayout';
import {
  hasAdminTabAccess,
  isSuperAdminEmail,
  normalizeAdminNavAccess,
  type AdminNavAccess,
} from './adminNavAccess';

const VALID_ADMIN_TABS = new Set<string>(ADMIN_TAB_OPTIONS.map((o) => o.value));
const VALID_REPORTS_VIEWS = new Set<string>(REPORTS_VIEW_OPTIONS.map((o) => o.value));

const getPlanRollupLabel = (user: Profile) => {
  const homeCount = user.home_count || 0;
  const managedHomeCount = user.managed_home_count || 0;

  if (managedHomeCount > 0) {
    return homeCount > 1 ? `Premium (${managedHomeCount} of ${homeCount} homes)` : 'Premium';
  }

  return homeCount > 1 ? `Basic (0 of ${homeCount} homes)` : 'Basic';
};

/**
 * Admin tabs that have been audited and are safe to render on iOS/Android.
 * Other tabs use raw DOM/Tailwind markup and would crash on native.
 *
 * To enable a new tab on native:
 *   1. Make the tab component use React Native primitives (View, Text, Pressable, etc.).
 *   2. Replace any `lucide-react`, `window.*`, `alert()`, `confirm()`, `<div>`/`<form>`/etc.
 *      with the cross-platform helpers in `src/lib/icons.ts` and `src/lib/platform.ts`.
 *   3. Add the tab value to this allowlist.
 */
const NATIVE_ADMIN_TABS = new Set<AdminTabValue>(['myday', 'conversations']);

const Admin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read tab from URL, default to 'myday'
  const tabFromUrl = searchParams.get('tab') as AdminTabValue | null;
  const [activeTab, setActiveTab] = useState<AdminTabValue>(
    tabFromUrl && VALID_ADMIN_TABS.has(tabFromUrl)
      ? (tabFromUrl as AdminTabValue)
      : 'myday'
  );

  // Reports sub-view (Overview vs Performance)
  const reportsViewFromUrl = searchParams.get('view');
  const [reportsView, setReportsView] = useState<ReportsViewValue>(
    reportsViewFromUrl && VALID_REPORTS_VIEWS.has(reportsViewFromUrl)
      ? (reportsViewFromUrl as ReportsViewValue)
      : 'overview'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [users, setUsers] = useState<Profile[]>([]);
  const [homeownerRequestCapacityByProfileId, setHomeownerRequestCapacityByProfileId] = useState<
    Record<string, { active_count: number; backburner_count: number }>
  >({});
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [testUserData, setTestUserData] = useState<{
    email: string;
    password: string;
    fullName: string;
    phoneNumber: string;
    lifecycle_status?: 'lead' | 'walkthrough' | 'first_visit' | 'active' | 'churned';
  }>({
    email: '',
    password: '',
    fullName: '',
    phoneNumber: ''
  });
  const [isSystemManager, setIsSystemManager] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminNavAccess, setAdminNavAccess] = useState<AdminNavAccess>({});
  const [accessLoaded, setAccessLoaded] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSentWelcomeCard, setFilterSentWelcomeCard] = useState<boolean | null>(null);
  const [filterHadWalkthrough, setFilterHadWalkthrough] = useState<boolean | null>(null);
  const [filterHasServiceRequests, setFilterHasServiceRequests] = useState<boolean | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Check if user is system manager
  useEffect(() => {
    const checkSystemManager = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_system_manager, admin_nav_access')
          .eq('id', user.id)
          .single();
        
        setIsSystemManager(profile?.is_system_manager || false);
        setIsSuperAdmin(isSuperAdminEmail(user.email));
        setAdminNavAccess(normalizeAdminNavAccess(profile?.admin_nav_access));
      }
      setAccessLoaded(true);
    };
    checkSystemManager();
  }, []);

  const visibleAdminTabs = useMemo(
    () =>
      ADMIN_TAB_OPTIONS.map((option) => option.value)
        .filter((tab) => hasAdminTabAccess(tab, adminNavAccess, isSuperAdmin))
        .filter((tab) => Platform.OS === 'web' || NATIVE_ADMIN_TABS.has(tab)),
    [adminNavAccess, isSuperAdmin]
  );

  // Keep active tab in sync when the URL changes (e.g. sidebar navigation in AdminLayout)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && VALID_ADMIN_TABS.has(t) && visibleAdminTabs.includes(t as AdminTabValue) && t !== activeTab) {
      setActiveTab(t as AdminTabValue);
    }
  }, [searchParams, activeTab, visibleAdminTabs]);

  useEffect(() => {
    if (!accessLoaded) return;
    if (!visibleAdminTabs.includes(activeTab)) {
      const fallbackTab = visibleAdminTabs[0] || 'myday';
      setActiveTab(fallbackTab);
    }
  }, [activeTab, visibleAdminTabs, accessLoaded]);

  // Update URL when tab changes
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const currentView = searchParams.get('view');

    const desiredView = activeTab === 'reports' ? reportsView : null;
    const tabChanged = currentTab !== activeTab;
    const viewChanged = (currentView || null) !== (desiredView || null);

    if (tabChanged || viewChanged) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', activeTab);
      if (desiredView) {
        newParams.set('view', desiredView);
      } else {
        newParams.delete('view');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, reportsView]);

  // Keep reportsView in sync when the URL changes
  useEffect(() => {
    if (activeTab !== 'reports') return;
    const v = searchParams.get('view');
    if (v && VALID_REPORTS_VIEWS.has(v) && v !== reportsView) {
      setReportsView(v as ReportsViewValue);
    }
  }, [searchParams, activeTab, reportsView]);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'pipeline') {
      fetchUsers();
    }
  }, [activeTab, filterSentWelcomeCard, filterHadWalkthrough, filterHasServiceRequests, filterStartDate, filterEndDate]);

  // Debounce search query
  useEffect(() => {
    if (activeTab === 'users') {
      const timeoutId = setTimeout(() => {
        fetchUsers();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // If there's a search query, use the omni search RPC to get matching profile IDs
      let searchMatchIds: string[] | null = null;
      if (searchQuery.trim()) {
        const trimmedQuery = searchQuery.trim();
        const { data: searchResults, error: searchError } = await supabase
          .rpc('search_users_omni', { search_term: trimmedQuery });

        if (searchError) {
          // Fallback for environments where the RPC migration has not been applied yet.
          if (searchError.code === 'PGRST202') {
            const escapedTerm = trimmedQuery.replace(/,/g, ' ');
            const likeTerm = `%${escapedTerm}%`;

            const [{ data: matchingProfiles, error: profilesSearchError }, { data: matchingHomes, error: homesSearchError }] =
              await Promise.all([
                supabase
                  .from('profiles')
                  .select('id')
                  .or(`full_name.ilike.${likeTerm},phone_number.ilike.${likeTerm}`),
                supabase
                  .from('homes')
                  .select('owner_id')
                  .or(`address.ilike.${likeTerm},city.ilike.${likeTerm},state.ilike.${likeTerm},zip_code.ilike.${likeTerm}`),
              ]);

            if (profilesSearchError) throw profilesSearchError;
            if (homesSearchError) throw homesSearchError;

            const profileIds = new Set<string>((matchingProfiles || []).map((row: { id: string }) => row.id));
            (matchingHomes || []).forEach((home: { owner_id: string | null }) => {
              if (home.owner_id) profileIds.add(home.owner_id);
            });

            searchMatchIds = Array.from(profileIds);
          } else {
            throw searchError;
          }
        } else {
          searchMatchIds = (searchResults || []).map((r: { profile_id: string }) => r.profile_id);
        }

        if (searchMatchIds.length === 0) {
          setUsers([]);
          setHomeownerRequestCapacityByProfileId({});
          return;
        }
      }

      let query = supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          phone_number,
          created_at,
          plan,
          is_system_manager,
          home_manager,
          home_manager_assigned_at,
          home_manager_assigned_by,
          lifecycle_status,
          lifecycle_updated_at,
          lifecycle_notes,
          profile_photo_url,
          sent_welcome_card_at,
          had_walkthrough
        `);

      // Restrict to search-matched profiles
      if (searchMatchIds) {
        query = query.in('id', searchMatchIds);
      }

      if (filterSentWelcomeCard !== null) {
        if (filterSentWelcomeCard) {
          query = query.not('sent_welcome_card_at', 'is', null);
        } else {
          query = query.is('sent_welcome_card_at', null);
        }
      }

      if (filterHadWalkthrough !== null) {
        query = query.eq('had_walkthrough', filterHadWalkthrough);
      }

      if (filterStartDate) {
        const startDateTime = new Date(filterStartDate);
        startDateTime.setHours(0, 0, 0, 0);
        query = query.gte('created_at', startDateTime.toISOString());
      }

      if (filterEndDate) {
        const endDateTime = new Date(filterEndDate);
        endDateTime.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDateTime.toISOString());
      }

      query = query.order('created_at', { ascending: false });

      const { data: profiles, error: profilesError } = await query;

      if (profilesError) throw profilesError;

      let filteredProfiles = (profiles || []).map(profile => ({
        ...profile,
        email: ''
      }));

      // Filter by service requests if needed
      if (filterHasServiceRequests !== null) {
        const profileIds = filteredProfiles.map(p => p.id);
        const { data: homes } = await supabase
          .from('homes')
          .select('id, owner_id')
          .in('owner_id', profileIds);

        const ownerIdsWithHomes = new Set(homes?.map(h => h.owner_id) || []);
        
        if (ownerIdsWithHomes.size > 0) {
          const { data: serviceRequests } = await supabase
            .from('service_requests')
            .select('home_id')
            .in('home_id', homes?.map(h => h.id) || []);

          const homeIdsWithRequests = new Set(serviceRequests?.map(sr => sr.home_id) || []);
          const ownerIdsWithRequests = new Set(
            homes?.filter(h => homeIdsWithRequests.has(h.id)).map(h => h.owner_id) || []
          );

          filteredProfiles = filteredProfiles.filter(profile => {
            const hasRequests = ownerIdsWithRequests.has(profile.id);
            return filterHasServiceRequests ? hasRequests : !hasRequests;
          });
        } else {
          if (filterHasServiceRequests) {
            filteredProfiles = [];
          }
        }
      }

      if (filteredProfiles.length > 0) {
        const profileIds = filteredProfiles.map((profile) => profile.id);
        const { data: planHomes, error: planHomesError } = await supabase
          .from('homes')
          .select('owner_id, plan')
          .in('owner_id', profileIds);

        if (planHomesError) throw planHomesError;

        const rollups = (planHomes || []).reduce(
          (acc, home) => {
            if (!home.owner_id) return acc;
            if (!acc[home.owner_id]) {
              acc[home.owner_id] = { home_count: 0, managed_home_count: 0 };
            }
            acc[home.owner_id].home_count += 1;
            if (home.plan === 'managed') {
              acc[home.owner_id].managed_home_count += 1;
            }
            return acc;
          },
          {} as Record<string, { home_count: number; managed_home_count: number }>
        );

        filteredProfiles = filteredProfiles.map((profile) => ({
          ...profile,
          home_count: rollups[profile.id]?.home_count || 0,
          managed_home_count: rollups[profile.id]?.managed_home_count || 0,
        }));
      }

      let capacityByProfile: Record<string, { active_count: number; backburner_count: number }> = {};
      try {
        const { data: capacityData, error: capacityError } = await supabase.rpc(
          'admin_homeowner_request_capacity'
        );
        if (!capacityError && capacityData) {
          capacityByProfile = (capacityData as { profile_id: string; active_count: number; backburner_count: number }[]).reduce(
            (acc, item) => {
              acc[item.profile_id] = {
                active_count: item.active_count ?? 0,
                backburner_count: item.backburner_count ?? 0,
              };
              return acc;
            },
            {} as Record<string, { active_count: number; backburner_count: number }>
          );
        } else if (capacityError) {
          console.error('Error fetching homeowner request capacity:', capacityError);
        }
      } catch (capacityErr) {
        console.error('Error fetching homeowner request capacity:', capacityErr);
      }
      setHomeownerRequestCapacityByProfileId(capacityByProfile);

      setUsers(filteredProfiles);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
      setHomeownerRequestCapacityByProfileId({});
    } finally {
      setLoading(false);
    }
  };

  const handleHomeManagerChange = async (userId: string, newManager: string) => {
    try {
      setUpdatingUserId(userId);
      setError(null);

      // Get the current user's session to ensure they're a system manager
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can update home managers');
      }

      const assignedAt = new Date().toISOString();

      // Update all homes for this owner (per-home manager model)
      const { error: updateHomesError } = await supabase
        .from('homes')
        .update({
          home_manager: newManager,
          home_manager_assigned_at: assignedAt,
          home_manager_assigned_by: user.id,
        })
        .eq('owner_id', userId);

      if (updateHomesError) throw updateHomesError;

      // Keep profile columns in sync during migration window
      const { error: updateProfileError } = await supabase
        .from('profiles')
        .update({
          home_manager: newManager,
          home_manager_assigned_at: assignedAt,
          home_manager_assigned_by: user.id,
        })
        .eq('id', userId);

      if (updateProfileError) throw updateProfileError;
      
      // Update the local state to reflect the change
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId 
            ? {
                ...u,
                home_manager: newManager,
                home_manager_assigned_at: assignedAt,
                home_manager_assigned_by: user.id
              }
            : u
        )
      );
      
      // Show success message
      setSuccess(`Home manager updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error updating home manager:', error);
      setError(`Failed to update home manager: ${error.message}`);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleLifecycleChange = async (userId: string, newStatus: Profile['lifecycle_status'], notes?: string) => {
    try {
      setUpdatingUserId(userId);
      setError(null);

      // Get the current user's session to ensure they're a system manager
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can update lifecycle status');
      }

      // Update the profile with the new status and return the updated row
      // First update the profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          lifecycle_status: newStatus,
          lifecycle_notes: notes
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Then fetch the updated profile
      const { data: updatedProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;
      
      console.log('Update response:', { updatedProfile, userId, newStatus });
      
      // Update the local state with the returned data
      if (!updatedProfile) throw new Error('No data returned from update');

      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId 
            ? {
                ...u,
                ...updatedProfile,  // Use the returned data from the server
                lifecycle_status: newStatus,
                lifecycle_notes: notes
              }
            : u
        )
      );
      
      setSuccess(`User status updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error updating lifecycle status:', error);
      setError(`Failed to update status: ${error.message}`);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleCreateTestUser = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const user = await createTestUser({
        email: testUserData.email,
        password: testUserData.password,
        fullName: testUserData.fullName,
        phoneNumber: testUserData.phoneNumber,
        lifecycle_status: 'lead' as const  // New users start as leads
      });
      setSuccess(`Test user created successfully! User ID: ${user.id}`);
      setTestUserData({
        email: '',
        password: '',
        fullName: '',
        phoneNumber: ''
      });
      
      if (activeTab === 'users') {
        fetchUsers();
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };


  const renderUsersList = () => {
    if (loading) {
      return (
        <View style={styles.usersListScroll}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#9ca3af" />
          </View>
        </View>
      );
    }

    if (users.length === 0) {
      return (
        <View style={styles.usersListScroll}>
          <View style={styles.emptyContainer}>
            <Users size={48} color="#9ca3af" />
            <Text style={styles.emptyTitle}>No Users Found</Text>
            <Text style={styles.emptyText}>There are no users in the system yet.</Text>
          </View>
        </View>
      );
    }

    return (
      <WebScrollView style={styles.usersListScroll}>
        <View style={styles.usersList}>
          {users.map((user) => (
            <View key={user.id} style={styles.userCard}>
              <View style={styles.userCardHeader}>
                <Pressable
                  onPress={() => navigate(`/admin/profile?id=${user.id}`)}
                  style={styles.userNameContainer}
                >
                  <Text style={styles.userName}>{user.full_name || 'N/A'}</Text>
                </Pressable>
                <View style={styles.badgesRow}>
                  <View style={[
                    styles.badge,
                    (user.managed_home_count || 0) > 0 ? styles.premiumBadge : styles.basicBadge
                  ]}>
                    <Text style={[
                      styles.badgeText,
                      (user.managed_home_count || 0) > 0 ? styles.premiumBadgeText : styles.basicBadgeText
                    ]}>
                      {getPlanRollupLabel(user)}
                    </Text>
                  </View>
                  <View style={[
                    styles.badge,
                    user.is_system_manager ? styles.managerBadge : styles.userBadge
                  ]}>
                    <Text style={[
                      styles.badgeText,
                      user.is_system_manager ? styles.managerBadgeText : styles.userBadgeText
                    ]}>
                      {user.is_system_manager ? 'System Manager' : 'User'}
                    </Text>
                  </View>
                </View>
              </View>

              <View style={styles.userCardContent}>
                <View style={styles.infoRow}>
                  <View style={styles.infoItem}>
                    <Phone size={16} color="#6b7280" />
                    <Text style={styles.infoText}>{user.phone_number || 'N/A'}</Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Calendar size={16} color="#6b7280" />
                    <Text style={styles.infoText}>
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </Text>
                  </View>
                </View>

                <View style={styles.compactInfoRow}>
                  <View style={styles.compactInfoItem}>
                    <Text style={styles.compactLabel}>Lifecycle:</Text>
                    <Text style={styles.compactValue}>
                      {user.lifecycle_status ? user.lifecycle_status.charAt(0).toUpperCase() + user.lifecycle_status.slice(1).replace('_', ' ') : 'N/A'}
                    </Text>
                  </View>
                  <View style={styles.compactInfoItem}>
                    <Text style={styles.compactLabel}>Manager:</Text>
                    <Text style={styles.compactValue}>{user.home_manager || 'Unassigned'}</Text>
                  </View>
                </View>

                <View style={styles.requestQueueWidget}>
                  <View style={styles.requestQueueWidgetHeader}>
                    <ClipboardList size={14} color="#6b7280" />
                    <Text style={styles.requestQueueWidgetTitle}>Service requests</Text>
                  </View>
                  <View style={styles.requestQueueStats}>
                    <View style={styles.requestQueueStat}>
                      <Text style={styles.requestQueueStatLabel}>Active</Text>
                      <Text style={styles.requestQueueStatValue}>
                        {homeownerRequestCapacityByProfileId[user.id]?.active_count ?? 0}
                      </Text>
                    </View>
                    <View style={styles.requestQueueStatDivider} />
                    <View style={styles.requestQueueStat}>
                      <Text style={styles.requestQueueStatLabel}>Back burner</Text>
                      <Text style={[styles.requestQueueStatValue, styles.requestQueueStatValueMuted]}>
                        {homeownerRequestCapacityByProfileId[user.id]?.backburner_count ?? 0}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          ))}
        </View>
      </WebScrollView>
    );
  };

  const renderDebugTools = () => (
    <View style={styles.debugContainer}>
      <View style={styles.debugCard}>
        <Text style={styles.debugTitle}>Create Test User</Text>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Email</Text>
            <TextInput
              style={styles.formInput}
              value={testUserData.email}
              onChangeText={(value) => setTestUserData({ ...testUserData, email: value })}
              placeholder="test@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Password</Text>
            <TextInput
              style={styles.formInput}
              value={testUserData.password}
              onChangeText={(value) => setTestUserData({ ...testUserData, password: value })}
              placeholder="Enter password"
              secureTextEntry
            />
          </View>
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Full Name</Text>
            <TextInput
              style={styles.formInput}
              value={testUserData.fullName}
              onChangeText={(value) => setTestUserData({ ...testUserData, fullName: value })}
              placeholder="John Doe"
            />
          </View>
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Phone Number</Text>
            <TextInput
              style={styles.formInput}
              value={testUserData.phoneNumber}
              onChangeText={(value) => setTestUserData({ ...testUserData, phoneNumber: value })}
              placeholder="(555) 123-4567"
              keyboardType="phone-pad"
            />
          </View>

          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          {success && (
            <View style={styles.successContainer}>
              <Text style={styles.successText}>{success}</Text>
            </View>
          )}

          <Pressable
            style={[styles.submitButton, loading && styles.submitButtonDisabled]}
            onPress={handleCreateTestUser}
            disabled={loading}
          >
            <Text style={styles.submitButtonText}>
              {loading ? 'Creating...' : 'Create Test User'}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.adminPageRoot}>
    <AdminLayout
      activeTab={activeTab}
      visibleTabs={visibleAdminTabs}
      activeSubView={activeTab === 'reports' ? reportsView : null}
      onSubViewChange={(view) => {
        if (activeTab === 'reports' && VALID_REPORTS_VIEWS.has(view)) {
          setReportsView(view as ReportsViewValue);
        }
      }}
    >
          {activeTab === 'myday' && <MyDayTab />}
          {activeTab === 'conversations' && <ConversationsTab />}
          {/* All other tabs render raw DOM and are web-only. */}
          {Platform.OS === 'web' && (
            <>
              {activeTab === 'tasks' && <TasksTab />}
              {activeTab === 'configuration' && <ConfigurationTab />}
              {activeTab === 'ai' && <AIAdminAssistant isSystemManager={isSystemManager} />}
              {activeTab === 'api-keys' && <ApiKeysTab />}
              {activeTab === 'reports' && reportsView === 'overview' && <ReportsTab />}
              {activeTab === 'reports' && reportsView === 'performance' && <ReportsPerformance />}
              {activeTab === 'reports' && reportsView === 'standards' && <ReportsStandards />}
              {activeTab === 'referrals' && <ReferralsTab />}
              {activeTab === 'walkthrough' && <WalkthroughAvailabilityTab />}
              {activeTab === 'homes' && <HomesTab />}
              {activeTab === 'expenses' && <ExpensesTab />}
              {activeTab === 'announcements' && <AnnouncementsTab />}
              {activeTab === 'debug' && renderDebugTools()}
            </>
          )}
          {Platform.OS === 'web' && activeTab === 'pipeline' && (
            <PipelineView
              users={users}
              onUserUpdate={handleLifecycleChange}
              onUserClick={(userId) => navigate(`/admin/profile?id=${userId}`)}
            />
          )}
          {Platform.OS === 'web' && activeTab === 'users' && (
            <View style={styles.usersTabRoot}>
            <View style={styles.usersCard}>
              <View style={styles.usersHeader}>
                <View style={styles.usersHeaderLeft}>
                  <Users size={24} color="#9ca3af" />
                  <Text style={styles.usersTitle}>System Users</Text>
                </View>
                <Pressable
                  style={styles.refreshButton}
                  onPress={() => fetchUsers()}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color="#4b5563" />
                  ) : (
                    <Text style={styles.refreshButtonText}>Refresh</Text>
                  )}
                </Pressable>
              </View>

              {/* Search Bar */}
              <View style={styles.searchContainer}>
                <Search size={20} color="#6b7280" />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search by name, email, phone, or address..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholderTextColor="#9ca3af"
                />
                {searchQuery.length > 0 && (
                  <Pressable
                    onPress={() => {
                      setSearchQuery('');
                      fetchUsers();
                    }}
                    style={styles.clearSearchButton}
                  >
                    <X size={16} color="#6b7280" />
                  </Pressable>
                )}
              </View>

              {/* Filters - Collapsible */}
              <View style={styles.filtersSection}>
                <Pressable
                  style={styles.filtersHeader}
                  onPress={() => setFiltersExpanded(!filtersExpanded)}
                >
                  <View style={styles.filtersHeaderLeft}>
                    <Filter size={20} color="#6b7280" />
                    <Text style={styles.filtersHeaderText}>Filters</Text>
                    {(filterSentWelcomeCard !== null || filterHadWalkthrough !== null || filterHasServiceRequests !== null || filterStartDate || filterEndDate) && (
                      <View style={styles.activeFiltersBadge}>
                        <Text style={styles.activeFiltersBadgeText}>
                          {[
                            filterSentWelcomeCard !== null,
                            filterHadWalkthrough !== null,
                            filterHasServiceRequests !== null,
                            !!filterStartDate,
                            !!filterEndDate
                          ].filter(Boolean).length}
                        </Text>
                      </View>
                    )}
                  </View>
                  {filtersExpanded ? (
                    <ChevronUp size={20} color="#6b7280" />
                  ) : (
                    <ChevronDown size={20} color="#6b7280" />
                  )}
                </Pressable>

                {filtersExpanded && (
                  <View style={styles.filtersContainer}>
                    <View style={styles.filterRow}>
                      <View style={styles.filterGroup}>
                        <Text style={styles.filterLabel}>Sent Welcome Card</Text>
                        <View style={styles.pickerContainer}>
                          <Picker
                            selectedValue={filterSentWelcomeCard === null ? 'all' : filterSentWelcomeCard ? 'yes' : 'no'}
                            onValueChange={(value) => {
                              setFilterSentWelcomeCard(value === 'all' ? null : value === 'yes');
                            }}
                            style={styles.filterPicker}
                          >
                            <Picker.Item label="All" value="all" />
                            <Picker.Item label="Yes" value="yes" />
                            <Picker.Item label="No" value="no" />
                          </Picker>
                        </View>
                      </View>

                      <View style={styles.filterGroup}>
                        <Text style={styles.filterLabel}>Had Walkthrough</Text>
                        <View style={styles.pickerContainer}>
                          <Picker
                            selectedValue={filterHadWalkthrough === null ? 'all' : filterHadWalkthrough ? 'yes' : 'no'}
                            onValueChange={(value) => {
                              setFilterHadWalkthrough(value === 'all' ? null : value === 'yes');
                            }}
                            style={styles.filterPicker}
                          >
                            <Picker.Item label="All" value="all" />
                            <Picker.Item label="Yes" value="yes" />
                            <Picker.Item label="No" value="no" />
                          </Picker>
                        </View>
                      </View>

                      <View style={styles.filterGroup}>
                        <Text style={styles.filterLabel}>Has Service Requests</Text>
                        <View style={styles.pickerContainer}>
                          <Picker
                            selectedValue={filterHasServiceRequests === null ? 'all' : filterHasServiceRequests ? 'yes' : 'no'}
                            onValueChange={(value) => {
                              setFilterHasServiceRequests(value === 'all' ? null : value === 'yes');
                            }}
                            style={styles.filterPicker}
                          >
                            <Picker.Item label="All" value="all" />
                            <Picker.Item label="Yes" value="yes" />
                            <Picker.Item label="No" value="no" />
                          </Picker>
                        </View>
                      </View>
                    </View>

                    {/* Date Range Filter */}
                    <View style={styles.dateRangeRow}>
                      <View style={styles.dateFilterGroup}>
                        <Text style={styles.filterLabel}>Sign Up Date - Start</Text>
                        {Platform.OS === 'web' ? (
                          <input
                            type="date"
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                            style={{
                              padding: '8px 12px',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: '#d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              color: '#111827',
                              backgroundColor: '#ffffff',
                              width: '100%',
                              fontFamily: 'inherit',
                              outlineStyle: 'none',
                            }}
                          />
                        ) : (
                          <TextInput
                            style={styles.dateInput}
                            value={filterStartDate}
                            onChangeText={setFilterStartDate}
                            placeholder="YYYY-MM-DD"
                            placeholderTextColor="#9ca3af"
                          />
                        )}
                      </View>

                      <View style={styles.dateFilterGroup}>
                        <Text style={styles.filterLabel}>Sign Up Date - End</Text>
                        {Platform.OS === 'web' ? (
                          <input
                            type="date"
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                            style={{
                              padding: '8px 12px',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: '#d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              color: '#111827',
                              backgroundColor: '#ffffff',
                              width: '100%',
                              fontFamily: 'inherit',
                              outlineStyle: 'none',
                            }}
                          />
                        ) : (
                          <TextInput
                            style={styles.dateInput}
                            value={filterEndDate}
                            onChangeText={setFilterEndDate}
                            placeholder="YYYY-MM-DD"
                            placeholderTextColor="#9ca3af"
                          />
                        )}
                      </View>
                    </View>

                    {/* Clear Filters Button */}
                    {(filterSentWelcomeCard !== null || filterHadWalkthrough !== null || filterHasServiceRequests !== null || filterStartDate || filterEndDate) && (
                      <Pressable
                        style={styles.clearFiltersButton}
                        onPress={() => {
                          setFilterSentWelcomeCard(null);
                          setFilterHadWalkthrough(null);
                          setFilterHasServiceRequests(null);
                          setFilterStartDate('');
                          setFilterEndDate('');
                        }}
                      >
                        <X size={16} color="#6b7280" />
                        <Text style={styles.clearFiltersText}>Clear Filters</Text>
                      </Pressable>
                    )}
                  </View>
                )}
              </View>

              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}
              {success && (
                <View style={styles.successContainer}>
                  <Text style={styles.successText}>{success}</Text>
                </View>
              )}
              {renderUsersList()}
            </View>
            </View>
          )}
          {Platform.OS === 'web' && (
            <>
              {activeTab === 'requests' && <ServiceRequestsTab />}
              {activeTab === 'quotes' && <MarketResearchQuotes />}
              {activeTab === 'visits' && <VisitsTab />}
              {activeTab === 'team' && <TeamTab />}
              {activeTab === 'agents' && <AgentsTab />}
              {activeTab === 'providers' && <ServiceProvidersTab />}
              {activeTab === 'import' && <PropertyImport />}
            </>
          )}
    </AdminLayout>
    </View>
  );
};

const styles = StyleSheet.create({
  adminPageRoot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    ...(Platform.OS === 'web' && { height: '100%' as const }),
  },
  /* ── Kept for Picker usage inside filter sections ── */
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    width: '100%',
  },
  picker: {
    height: Platform.OS === 'ios' ? 200 : 50,
    width: '100%',
  },
  usersTabRoot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  usersCard: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 16,
    ...shadows.sm,
  },
  usersListScroll: {
    flex: 1,
    minHeight: 0,
  },
  usersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  usersHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  usersTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
    minHeight: 200,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    minHeight: 200,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
  },
  usersList: {
    gap: 16,
  },
  userCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    ...shadows.sm,
  },
  userCardHeader: {
    marginBottom: 16,
    gap: 12,
  },
  userNameContainer: {
    marginBottom: 4,
  },
  userName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
    textDecorationLine: 'underline',
  },
  badgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  userCardContent: {
    gap: 12,
  },
  infoRow: {
    gap: 12,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#374151',
  },
  fieldGroup: {
    gap: 8,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  premiumBadge: {
    backgroundColor: '#000000',
  },
  premiumBadgeText: {
    color: '#ffffff',
  },
  basicBadge: {
    backgroundColor: '#f3f4f6',
  },
  basicBadgeText: {
    color: '#1f2937',
  },
  managerBadge: {
    backgroundColor: '#dbeafe',
  },
  managerBadgeText: {
    color: '#1e40af',
  },
  userBadge: {
    backgroundColor: '#f3f4f6',
  },
  userBadgeText: {
    color: '#1f2937',
  },
  updatedText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  successContainer: {
    backgroundColor: '#d1fae5',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  successText: {
    color: '#065f46',
    fontSize: 14,
  },
  debugContainer: {
    gap: 24,
  },
  debugCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    padding: 24,
  },
  debugTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 16,
  },
  form: {
    gap: 16,
  },
  formGroup: {
    gap: 4,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  submitButton: {
    backgroundColor: '#000000',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  compactInfoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 8,
  },
  compactInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  compactLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
  },
  compactValue: {
    fontSize: 13,
    color: '#111827',
    fontWeight: '500',
  },
  requestQueueWidget: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  requestQueueWidgetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  requestQueueWidgetTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  requestQueueStats: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  requestQueueStat: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  requestQueueStatDivider: {
    width: 1,
    backgroundColor: '#e5e7eb',
  },
  requestQueueStatLabel: {
    fontSize: 11,
    fontWeight: '500',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  requestQueueStatValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  requestQueueStatValueMuted: {
    color: '#6b7280',
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    marginBottom: 16,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    paddingVertical: 4,
  },
  clearSearchButton: {
    padding: 4,
  },
  filtersSection: {
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  filtersHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  filtersHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filtersHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  activeFiltersBadge: {
    backgroundColor: '#3b82f6',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeFiltersBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ffffff',
  },
  filtersContainer: {
    padding: 16,
    gap: 12,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  filterGroup: {
    flex: 1,
    minWidth: 150,
    gap: 4,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  filterPicker: {
    height: Platform.OS === 'ios' ? 200 : 50,
    width: '100%',
  },
  clearFiltersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  clearFiltersText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  dateRangeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  dateFilterGroup: {
    flex: 1,
    minWidth: 200,
    gap: 4,
  },
  dateInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
});

export default Admin;
